import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertCircle,
  ArrowLeft,
  Ban,
  ImageOff,
  Loader2,
  Pencil,
  Play,
  Plus,
  Search,
  X,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { BusyOverlay } from '@/components/ui/busy-overlay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/layout/page-header';
import { useBusinesses } from '@/features/business/use-businesses';
// The catalogue picker already exists for modifier options; a second copy of a
// product search is not worth the independence.
import { useCatalogSearch } from '@/features/modifiers/modifiers.api';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';
import { SponsorshipEditor } from './sponsorship-editor';
import {
  useAddBusiness,
  useAddItem,
  useApplySponsorship,
  useCancelSponsorship,
  useRemoveBusiness,
  useRemoveItem,
  useSponsorship,
  useSponsorshipBusinesses,
  useSponsorshipItems,
} from './sponsorships.api';
import { statusOf } from './types';

/**
 * One sponsorship: its window, the stores it covers, the items it covers, and
 * the two buttons that push it to the catalogue.
 *
 * Apply and Cancel are the whole point and the original explained neither. They
 * are not "activate" and "deactivate" on this record — they walk every
 * item × store pair and write the window onto each `BusinessItem`, and Cancel
 * back-dates those same pairs by ten days. Nothing here records that it
 * happened, so the page states what the buttons do instead of showing a state
 * it cannot read back.
 */

export function SponsorshipDetailPage() {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const navigate = useNavigate();

  const [editing, setEditing] = useState(false);
  const [storeToAdd, setStoreToAdd] = useState('');
  const [itemQuery, setItemQuery] = useState('');

  const sponsorship = useSponsorship(id);
  const items = useSponsorshipItems(id);
  const stores = useSponsorshipBusinesses(id);
  const { data: businesses } = useBusinesses();

  const catalogSearch = useCatalogSearch();
  const addItem = useAddItem(id);
  const removeItem = useRemoveItem(id);
  const addBusiness = useAddBusiness(id);
  const removeBusiness = useRemoveBusiness(id);
  const apply = useApplySponsorship();
  const cancel = useCancelSponsorship();

  const record = sponsorship.data;
  const assignedItems = items.data ?? [];
  const assignedStores = stores.data ?? [];
  const running = apply.isPending || cancel.isPending;

  const failed = (error: unknown) =>
    toast.error(
      error instanceof Error ? error.message : t('sponsorships.actionFailed')
    );

  /** Stores not already on the sponsorship. */
  const available = (businesses ?? []).filter(
    (business) => !assignedStores.some((store) => store.id === business.id)
  );

  const pairCount = assignedItems.length * assignedStores.length;
  const canApply = pairCount > 0;

  if (sponsorship.isError) {
    return (
      <div className="space-y-4">
        <Button variant="outline" size="sm" asChild>
          <Link to="/sponsorships">
            <ArrowLeft className="size-4" />
            {t('sponsorships.backToList')}
          </Link>
        </Button>
        <div className="border-destructive/30 bg-destructive/8 text-destructive flex items-start gap-2 rounded-xl border p-4 text-sm">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          <p>{t('sponsorships.detailFailed')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Button variant="ghost" size="sm" className="-ml-2" asChild>
        <Link to="/sponsorships">
          <ArrowLeft className="size-4" />
          {t('sponsorships.backToList')}
        </Link>
      </Button>

      {sponsorship.isPending ? (
        <div className="space-y-2">
          <Skeleton className="h-7 w-56" />
          <Skeleton className="h-4 w-72" />
        </div>
      ) : (
        record && (
          <PageHeader
            title={record.name || '—'}
            description={
              <span className="flex flex-wrap items-center gap-2">
                <span>
                  {formatDate(record.startDate)} – {formatDate(record.endDate)}
                </span>
                <Badge
                  variant={
                    statusOf(record) === 'running'
                      ? 'success'
                      : statusOf(record) === 'scheduled'
                        ? 'secondary'
                        : 'outline'
                  }
                >
                  {t(
                    `sponsorships.status${statusOf(record)[0].toUpperCase()}${statusOf(record).slice(1)}`
                  )}
                </Badge>
              </span>
            }
            actions={
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(true)}
              >
                <Pencil className="size-3.5" />
                {t('sponsorships.edit')}
              </Button>
            }
          />
        )
      )}

      {/* ------------------------------------------------- apply / cancel */}
      <div className="bg-card space-y-3 rounded-xl border p-4">
        <div>
          <h3 className="text-sm font-semibold">
            {t('sponsorships.pushToCatalogue')}
          </h3>
          <p className="text-muted-foreground mt-1 text-sm text-pretty">
            {t('sponsorships.pushExplainer', { count: pairCount })}
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button size="sm" disabled={!canApply || running}>
                {apply.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <Play className="size-3.5" />
                )}
                {t('sponsorships.apply')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('sponsorships.apply')}</AlertDialogTitle>
                <AlertDialogDescription>
                  {t('sponsorships.applyConfirm', { count: pairCount })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  onClick={() =>
                    apply.mutate(id, {
                      onSuccess: () => toast.success(t('sponsorships.applied')),
                      onError: failed,
                    })
                  }
                >
                  {t('sponsorships.apply')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                disabled={!canApply || running}
              >
                {cancel.isPending ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <Ban className="size-3.5" />
                )}
                {t('sponsorships.cancelRun')}
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>
                  {t('sponsorships.cancelRun')}
                </AlertDialogTitle>
                <AlertDialogDescription>
                  {t('sponsorships.cancelConfirm', { count: pairCount })}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  onClick={() =>
                    cancel.mutate(id, {
                      onSuccess: () =>
                        toast.success(t('sponsorships.cancelled')),
                      onError: failed,
                    })
                  }
                >
                  {t('sponsorships.cancelRun')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>

        {!canApply && (
          <p className="text-muted-foreground text-xs">
            {t('sponsorships.needBoth')}
          </p>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {/* ------------------------------------------------------ stores */}
        <section className="space-y-3">
          <h3 className="text-xs font-semibold tracking-wide uppercase">
            {t('sponsorships.stores', { count: assignedStores.length })}
          </h3>

          <div className="flex gap-2">
            <Select
              value={storeToAdd}
              disabled={available.length === 0 || addBusiness.isPending}
              onValueChange={setStoreToAdd}
            >
              <SelectTrigger className="w-full">
                <SelectValue placeholder={t('sponsorships.chooseStore')} />
              </SelectTrigger>
              <SelectContent>
                {available.map((business) => (
                  <SelectItem key={business.id} value={business.id}>
                    {business.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button
              disabled={!storeToAdd || addBusiness.isPending}
              onClick={() =>
                addBusiness.mutate(storeToAdd, {
                  onSuccess: () => setStoreToAdd(''),
                  onError: failed,
                })
              }
            >
              {addBusiness.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                <Plus className="size-4" />
              )}
              {t('sponsorships.add')}
            </Button>
          </div>

          {stores.isPending && <Skeleton className="h-24 w-full rounded-xl" />}

          {stores.isSuccess && assignedStores.length === 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={stores.isFetching} />
              <p className="text-muted-foreground rounded-xl border border-dashed py-8 text-center text-sm">
                {t('sponsorships.noStores')}
              </p>
            </div>
          )}

          {assignedStores.length > 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={stores.isFetching} />
              <div
                className={cn(
                  'divide-y rounded-xl border',
                  stores.isFetching && 'opacity-60 transition-opacity'
                )}
              >
                {assignedStores.map((store) => (
                  <div
                    key={store.id}
                    className="flex items-center gap-2 p-3 text-sm"
                  >
                    <span className="min-w-0 flex-1 truncate">{store.name}</span>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('sponsorships.remove')}
                      disabled={removeBusiness.isPending}
                      onClick={() =>
                        removeBusiness.mutate(store.id, { onError: failed })
                      }
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* ------------------------------------------------------- items */}
        <section className="space-y-3">
          <h3 className="text-xs font-semibold tracking-wide uppercase">
            {t('sponsorships.items', { count: assignedItems.length })}
          </h3>

          <form
            role="search"
            className="flex gap-2"
            onSubmit={(event) => {
              event.preventDefault();
              if (itemQuery.trim()) catalogSearch.mutate(itemQuery.trim());
            }}
          >
            <div className="relative flex-1">
              <Search className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2" />
              <Input
                value={itemQuery}
                onChange={(event) => setItemQuery(event.target.value)}
                placeholder={t('sponsorships.searchItems')}
                aria-label={t('sponsorships.searchItems')}
                className="pl-9"
              />
            </div>
            <Button type="submit" disabled={catalogSearch.isPending}>
              {catalogSearch.isPending ? (
                <Loader2 className="animate-spin" />
              ) : (
                t('common.search')
              )}
            </Button>
          </form>

          {catalogSearch.isSuccess &&
            catalogSearch.data.items.length === 0 && (
              <p className="text-muted-foreground text-xs">
                {t('sponsorships.noItemsFound')}
              </p>
            )}

          {catalogSearch.data && catalogSearch.data.items.length > 0 && (
            <div className="max-h-56 divide-y overflow-y-auto rounded-xl border">
              {catalogSearch.data.items.map((item) => {
                const already = assignedItems.some(
                  (assigned) => assigned.id === item.id
                );
                return (
                  <div
                    key={item.id}
                    className="flex items-center gap-2.5 p-2.5 text-sm"
                  >
                    {item.itemImage ? (
                      <img
                        src={item.itemImage}
                        alt=""
                        className="bg-muted size-9 shrink-0 rounded-md object-contain"
                      />
                    ) : (
                      <div className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-md">
                        <ImageOff className="size-3.5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate">{item.name || '—'}</p>
                      <p className="text-muted-foreground truncate text-xs">
                        {item.brand}
                      </p>
                    </div>
                    <Button
                      size="sm"
                      variant={already ? 'outline' : 'default'}
                      disabled={already || addItem.isPending}
                      onClick={() => addItem.mutate(item.id, { onError: failed })}
                    >
                      {already
                        ? t('sponsorships.added')
                        : t('sponsorships.add')}
                    </Button>
                  </div>
                );
              })}
            </div>
          )}

          {items.isPending && <Skeleton className="h-24 w-full rounded-xl" />}

          {items.isSuccess && assignedItems.length === 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={items.isFetching} />
              <p className="text-muted-foreground rounded-xl border border-dashed py-8 text-center text-sm">
                {t('sponsorships.noItems')}
              </p>
            </div>
          )}

          {assignedItems.length > 0 && (
            <div className="relative rounded-xl">
              <BusyOverlay show={items.isFetching} />
              <div
                className={cn(
                  'divide-y rounded-xl border',
                  items.isFetching && 'opacity-60 transition-opacity'
                )}
              >
                {assignedItems.map((item) => (
                  <div
                    key={item.id}
                    className="flex items-center gap-2.5 p-3 text-sm"
                  >
                    {item.itemThumbnail || item.itemImage ? (
                      <img
                        src={item.itemThumbnail ?? item.itemImage ?? ''}
                        alt=""
                        className="bg-muted size-9 shrink-0 rounded-md object-contain"
                      />
                    ) : (
                      <div className="bg-muted text-muted-foreground flex size-9 shrink-0 items-center justify-center rounded-md">
                        <ImageOff className="size-3.5" />
                      </div>
                    )}
                    <div className="min-w-0 flex-1">
                      <p className="truncate">{item.name || '—'}</p>
                      <p className="text-muted-foreground truncate text-xs">
                        {[item.brand, item.gtin].filter(Boolean).join(' · ')}
                      </p>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('sponsorships.remove')}
                      disabled={removeItem.isPending}
                      onClick={() => removeItem.mutate(item.id, { onError: failed })}
                    >
                      <X className="size-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>
      </div>

      <SponsorshipEditor
        sponsorship={
          record
            ? {
                id: record.id,
                name: record.name ?? '',
                startDate: record.startDate,
                endDate: record.endDate,
              }
            : null
        }
        open={editing}
        onOpenChange={setEditing}
        onDeleted={() => navigate('/sponsorships')}
      />
    </div>
  );
}
